import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { register } from '../api/auth';
import { getList } from '../api/companies';
import { checkDuplicate } from '../api/companies';
import { toast } from '../components/common/Toast';
import SmsVerifyStep from '../components/SmsVerifyStep';
import { Company } from '../types';
import { formatPhoneInput, isValidPhone } from '../utils/phone';
import './Pages.css';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');

  const [smsVerified, setSmsVerified] = useState(false);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyMode, setCompanyMode] = useState<'select' | 'other' | 'none'>('none');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [otherCompanyName, setOtherCompanyName] = useState('');
  const [duplicateWarn, setDuplicateWarn] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getList({ limit: 200 }).then((res) => setCompanies(res.items)).catch(() => {});
  }, []);

  const handleStep1Next = () => {
    if (!isValidPhone(phone)) {
      setError('올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)');
      return;
    }
    if (password.length < 8) {
      setError('비밀번호는 8자 이상이어야 합니다');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다');
      return;
    }
    if (!name.trim()) {
      setError('이름을 입력해주세요');
      return;
    }
    const nicknameTrimmed = nickname.trim();
    if (nicknameTrimmed && (nicknameTrimmed.length < 2 || nicknameTrimmed.length > 20)) {
      setError('닉네임은 2~20자여야 합니다 (또는 비워두세요)');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSmsVerified = () => {
    setSmsVerified(true);
    setStep(3);
  };

  const handleOtherCompanyCheck = async (value: string) => {
    setOtherCompanyName(value);
    setDuplicateWarn('');
    if (value.trim().length < 2) return;
    try {
      const result = await checkDuplicate(value.trim());
      if (result.exists && result.is_approved) {
        setDuplicateWarn('이미 등록된 회사입니다. 리스트에서 선택해주세요');
      } else if (result.exists && !result.is_approved) {
        setDuplicateWarn('이미 승인 대기 중인 회사입니다. 동일 회사에 합류됩니다');
      }
    } catch { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!smsVerified) {
      setError('SMS 인증을 완료해주세요');
      return;
    }
    if (companyMode === 'other' && duplicateWarn.includes('리스트에서')) {
      setError('이미 등록된 회사입니다. 리스트에서 선택하거나 다른 이름을 입력해주세요');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await register({
        phone,
        password,
        name: name.trim(),
        nickname: nickname.trim() || undefined,
        company_id: companyMode === 'select' && selectedCompanyId ? selectedCompanyId : undefined,
        company_name: companyMode === 'other' && otherCompanyName.trim() ? otherCompanyName.trim() : undefined,
      });
      sessionStorage.setItem('auth_redirect_message', '가입이 완료되었습니다. 로그인해주세요');
      navigate('/login');
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)?.error
        : undefined;
      toast.error(msg || '가입에 실패했습니다');
      setError(msg || '가입에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-form" style={{ maxWidth: 440 }}>
        <div className="login-logo">NUCODE</div>
        <h2 className="login-title">회원가입</h2>

        {/* Step 1: 기본 정보 */}
        {step >= 1 && (
          <div style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>전화번호 (로그인 ID)</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="010-0000-0000"
                maxLength={13}
                disabled={step > 1}
              />
            </div>
            <div className="form-group">
              <label>비밀번호 (8자 이상, 대/소문자+숫자+특수)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                disabled={step > 1}
              />
            </div>
            <div className="form-group">
              <label>비밀번호 확인</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호 확인"
                disabled={step > 1}
              />
            </div>
            <div className="form-group">
              <label>이름</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="이름"
                disabled={step > 1}
              />
            </div>
            <div className="form-group">
              <label>닉네임 (선택)</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="비우면 이름이 닉네임으로 사용됩니다"
                maxLength={20}
                disabled={step > 1}
              />
            </div>
            {step === 1 && (
              <button
                type="button"
                className="btn btn--primary login-btn"
                onClick={handleStep1Next}
              >
                다음
              </button>
            )}
          </div>
        )}

        {/* Step 2: SMS 인증 */}
        {step >= 2 && (
          <div style={{ marginBottom: 16, padding: '12px 0', borderTop: '1px solid #eee' }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>SMS 인증</label>
            <SmsVerifyStep
              phone={phone}
              purpose="signup"
              onVerified={handleSmsVerified}
              disabled={smsVerified}
            />
          </div>
        )}

        {/* Step 3: 회사 선택 + 제출 */}
        {step >= 3 && (
          <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
              소속 회사 (선택)
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <label>
                <input
                  type="radio"
                  name="companyMode"
                  checked={companyMode === 'none'}
                  onChange={() => { setCompanyMode('none'); setDuplicateWarn(''); }}
                /> 없음 (외부자)
              </label>
              <label>
                <input
                  type="radio"
                  name="companyMode"
                  checked={companyMode === 'select'}
                  onChange={() => { setCompanyMode('select'); setDuplicateWarn(''); }}
                /> 리스트 선택
              </label>
              <label>
                <input
                  type="radio"
                  name="companyMode"
                  checked={companyMode === 'other'}
                  onChange={() => setCompanyMode('other')}
                /> 기타 입력
              </label>
            </div>

            {companyMode === 'select' && (
              <select
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                style={{ width: '100%', padding: 8 }}
              >
                <option value="">-- 회사 선택 --</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}

            {companyMode === 'other' && (
              <div>
                <input
                  type="text"
                  value={otherCompanyName}
                  onChange={(e) => handleOtherCompanyCheck(e.target.value)}
                  placeholder="회사명 입력"
                  style={{ width: '100%' }}
                />
                {duplicateWarn && (
                  <p style={{ color: duplicateWarn.includes('리스트') ? '#F44336' : '#FF9800', fontSize: 13, marginTop: 4 }}>
                    {duplicateWarn}
                  </p>
                )}
              </div>
            )}

            <button
              type="button"
              className="btn btn--primary login-btn"
              onClick={handleSubmit}
              disabled={loading}
              style={{ marginTop: 16 }}
            >
              {loading ? '가입 중...' : '가입하기'}
            </button>
          </div>
        )}

        {error && <p style={{ color: '#F44336', marginTop: 8, fontSize: 13 }}>{error}</p>}

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
          이미 계정이 있으신가요? <Link to="/login">로그인</Link>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
